import { Paper, Text } from "@mantine/core";
import IconInfoCircle from "@src/assets/icons/tabler/info-circle.svg?react";
import type { WhiteboardDocumentId } from "@src/components/collaboration/document-id";
import clsx from "clsx";
import { useId } from "react";
import { CollectNotesEmptyState } from "./collect-notes-empty-state";
import { LinkInputWithCopy } from "./link-input-with-copy";
import { SharePanelQrSection } from "./share-panel-qr-section";
import { getSharePanelQrFilename } from "./share-panel-qr-utils";
import { SwitchCard } from "./switch-card";

type CollectTabContentProps = {
	boardId: WhiteboardDocumentId;
	isPublished: boolean;
	isCollectNotesEnabled: boolean;
	isLoading: boolean;
	collectNotesLink: string | null;
	whiteboardName: string;
	isCollectLinkCopiedTooltipOpen: boolean;
	onPublishAndCollect: () => void;
	onCollectNotesSwitchChange: (checked: boolean) => void;
	onCopyTooltipTrigger: () => void;
};

export function CollectTabContent({
	boardId,
	isPublished,
	isCollectNotesEnabled,
	isLoading,
	collectNotesLink,
	whiteboardName,
	isCollectLinkCopiedTooltipOpen,
	onPublishAndCollect,
	onCollectNotesSwitchChange,
	onCopyTooltipTrigger,
}: CollectTabContentProps) {
	const collectQrCodeId = useId();
	const canShowQrCode = isCollectNotesEnabled && Boolean(collectNotesLink);

	if (!isPublished) {
		return (
			<CollectNotesEmptyState
				onPublish={onPublishAndCollect}
				isLoading={isLoading}
			/>
		);
	}

	return (
		<div className="space-y-6 pt-0">
			<SwitchCard
				title="Collect Notes via Link"
				description="Allow anyone with the link to send notes to this board"
				checked={isCollectNotesEnabled}
				onChange={onCollectNotesSwitchChange}
				disabled={isLoading}
			/>

			<div className="space-y-4">
				<Paper
					withBorder
					p="md"
					radius="md"
					className="bg-surface-subtle shadow-sm border-border-subtle transition-all duration-300 hover:shadow-md"
				>
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-2">
							<div className="flex justify-between items-end">
								<Text
									size="xs"
									fw={700}
									c="dimmed"
									className="uppercase tracking-widest"
								>
									Collection Form Link
								</Text>
							</div>
							<LinkInputWithCopy
								value={isCollectNotesEnabled ? collectNotesLink : null}
								placeholder={
									isLoading ? "" : "Enable collection to generate link"
								}
								disabled={isLoading || !isCollectNotesEnabled}
								isLoading={isLoading}
								isActive={isCollectNotesEnabled}
								tooltipOpen={isCollectLinkCopiedTooltipOpen}
								onTooltipTrigger={onCopyTooltipTrigger}
							/>
						</div>

						{canShowQrCode && collectNotesLink && (
							<SharePanelQrSection
								boardId={boardId}
								qrCodeId={collectQrCodeId}
								link={collectNotesLink}
								title="Scan to send notes to this board"
								description="Display this QR code on a screen so guests can join quickly without typing the collection link."
								filename={getSharePanelQrFilename(
									whiteboardName,
									"collect-notes-qr",
								)}
								downloadAriaLabel="Download collection link as SVG QR code"
								openAriaLabel="Open collection link in new tab"
								analyticsContext="collect_notes"
							/>
						)}

						{!canShowQrCode && (
							<div className="border-t border-border-subtle pt-4">
								<div className="flex gap-4 items-center">
									<div
										className={clsx(
											"flex h-10 w-10 shrink-0 items-center justify-center",
											"rounded-full bg-brand-primary-soft p-2.5 shadow-inner",
										)}
									>
										<IconInfoCircle className="w-5 h-5 text-brand-primary" />
									</div>
									<div className="flex flex-col gap-0.5">
										<Text size="sm" fw={700} className="text-text-primary">
											Pro Tip
										</Text>
										<Text size="xs" className="text-text-secondary" lh={1.4}>
											Enable note collection to generate a QR code and share the
											link for anonymous brainstorming.
										</Text>
									</div>
								</div>
							</div>
						)}
					</div>
				</Paper>
			</div>
		</div>
	);
}
